import React from 'react';
import { ExternalLink, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockSponsors } from '../mockData';
import SEOHead from '../components/SEOHead';

const TIER_STYLES = {
  Platinum: { badge: 'bg-gray-900 text-white', ring: 'ring-gray-300' },
  Gold:     { badge: 'bg-amber-900 text-white', ring: 'ring-amber-300' },
  Silver:   { badge: 'bg-slate-700 text-white', ring: 'ring-slate-300' },
  Bronze:   { badge: 'bg-amber-950 text-white', ring: 'ring-amber-300' },
};

const SponsorsMedia = () => {
  // Media Coverage and Press Kit sections are hidden
  // const mediaArticles = [
  //   {
  //     id: '1',
  //     title: 'DonorsHouse Connects Global Community to Save Lives',
  //     publication: 'Health Today Magazine',
  //     date: '2025-01-10',
  //     excerpt: 'Revolutionary platform brings together blood and organ donors from 195 countries...',
  //     url: '#'
  //   },
  //   {
  //     id: '2',
  //     title: 'How Technology is Transforming Organ Donation',
  //     publication: 'Medical Tech News',
  //     date: '2024-12-15',
  //     excerpt: 'Digital platforms like DonorsHouse are making it easier than ever to save lives...',
  //     url: '#'
  //   }
  // ];

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  return (
    <>
      <SEOHead
        title="Partners & Sponsors | DonorsHouse"
        description="Meet the organizations supporting DonorsHouse's global mission to connect blood and organ donors with those in need."
      />
    <div className="min-h-screen bg-white">
      {/* Hero banner */}
      <section className="relative overflow-hidden bg-gradient-to-br from-red-900 to-red-700 text-white py-20">
        <div className="absolute inset-0 opacity-5" aria-hidden="true"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/svg%3E")` }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-extrabold mb-4">Partners &amp; Sponsors</h1>
          <p className="text-xl text-red-100 max-w-2xl mx-auto">
            Organizations who believe in our mission to connect donors and save lives worldwide
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* New Abilities Foundation Banner */}
        <Card className="shadow-xl mb-16 border-red-100 overflow-hidden">
          <div className="bg-gradient-to-r from-red-50 to-white">
            <CardContent className="pt-10 pb-10">
              <div className="text-center">
                <Badge className="bg-red-700 text-white text-sm px-4 py-1.5 mb-5 font-semibold">
                  Primary Supporter
                </Badge>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-1">New Abilities Foundation</h2>
                <p className="text-lg text-gray-500 font-medium mb-4">A 501(c)(3) nonprofit organization</p>
                <p className="text-gray-700 mb-8 max-w-3xl mx-auto text-lg leading-relaxed">
                  New Abilities Foundation is dedicated to supporting global health initiatives and making
                  healthcare accessible to everyone. Through their generous support, DonorsHouse connects
                  donors and recipients worldwide.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a href="https://newabilities.org" target="_blank" rel="noopener noreferrer" aria-label="Visit New Abilities Foundation — opens in new tab">
                    <Button className="bg-gray-900 text-white hover:bg-gray-800 btn-transition">
                      <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
                      Visit Website
                    </Button>
                  </a>
                  <a href="https://newabilities.org/donate" target="_blank" rel="noopener noreferrer" aria-label="Donate to New Abilities Foundation — opens in new tab">
                    <Button className="bg-red-700 hover:bg-red-800 text-white btn-transition">
                      Support Their Mission
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* All Sponsors */}
        <section aria-label="Our sponsors">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-8">Our Sponsors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockSponsors.map((sponsor) => {
              const tier = TIER_STYLES[sponsor.tier] || TIER_STYLES.Bronze;
              return (
                <Card key={sponsor.id} className="shadow-md card-hover bg-white border-gray-100 group">
                  <CardHeader className="pb-3">
                    {/* Logo placeholder */}
                    <div className={`w-14 h-14 rounded-xl bg-gray-50 border-2 ${tier.ring} ring-1 flex items-center justify-center mb-3 group-hover:bg-red-50 transition-colors`}>
                      <Building2 className="h-7 w-7 text-gray-400 group-hover:text-red-500 transition-colors" aria-hidden="true" />
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={`${tier.badge} text-xs font-semibold`}>
                        {sponsor.tier}
                      </Badge>
                    </div>
                    <CardTitle className="text-gray-900 font-bold">{sponsor.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 text-sm mb-4 leading-relaxed">{sponsor.description}</p>
                    {sponsor.website && (
                      <a href={sponsor.website} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${sponsor.name} website — opens in new tab`}>
                        <Button variant="outline" size="sm" className="text-gray-800 border-gray-300 hover:border-red-400 hover:text-red-700 btn-transition">
                          <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
                          Visit
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default SponsorsMedia;
